// Full Calendar Plugins
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

// eslint-disable-next-line object-curly-newline
import { ref, computed, watch, onMounted } from "@vue/composition-api";
import store from "@/store";

export default function userCalendar() {
  // Use toast
  const toast = useToast();
  // ------------------------------------------------
  // refCalendar
  // ------------------------------------------------
  const refCalendar = ref(null);

  // ------------------------------------------------
  // calendarApi
  // ------------------------------------------------
  let calendarApi = null;
  onMounted(() => {
    calendarApi = refCalendar.value.getApi();
  });

  // ------------------------------------------------
  // calendars
  // ------------------------------------------------
  const calendarsColor = {
    Students: "primary",
    Holiday: "success",
    Staff: "danger",
    Parents: "warning",
    ETC: "info",
  };

  // ------------------------------------------------
  // event
  // ------------------------------------------------
  const blankEvent = {
    title: "",
    start: "",
    end: "",
    allDay: false,
    url: "",
    extendedProps: {
      calendar: "",
      guests: [],
      location: "",
      description: "",
      classID: [0],
      secID: [0],
    },
  };
  const event = ref(JSON.parse(JSON.stringify(blankEvent)));
  const clearEventData = () => {
    event.value = JSON.parse(JSON.stringify(blankEvent));
  };

  // *===========================================================================---*
  // *--------- Calendar API Function/Utils --------------------------------------------*
  // Template Future Update: We might move this utils function in its own file
  // *===========================================================================---*

  // ------------------------------------------------
  // (UI) addEventInCalendar
  // ? This is useless because this just add event in calendar and not in our data
  // * If we try to call it on new event then callback & try to toggle from calendar we get two events => One from UI and one from data
  // ------------------------------------------------
  // const addEventInCalendar = eventData => {
  //   toast({
  //     component: ToastificationContent,
  //     position: 'bottom-right',
  //     props: {
  //       title: 'Event Added',
  //       icon: 'CheckIcon',
  //       variant: 'success',
  //     },
  //   })
  //   calendarApi.addEvent(eventData)
  // }

  // ------------------------------------------------
  // (UI) updateEventInCalendar
  // ------------------------------------------------
  const updateEventInCalendar = (
    updatedEventData,
    propsToUpdate,
    extendedPropsToUpdate
  ) => {
    toast({
      component: ToastificationContent,
      props: {
        title: "Event Updated",
        icon: "CheckIcon",
        variant: "success",
      },
    });

    const existingEvent = calendarApi.getEventById(updatedEventData.id);

    // --- Set event properties except date related ----- //
    // ? Docs: https://fullcalendar.io/docs/Event-setProp
    // dateRelatedProps => ['start', 'end', 'allDay']
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < propsToUpdate.length; index++) {
      const propName = propsToUpdate[index];
      existingEvent.setProp(propName, updatedEventData[propName]);
    }

    // --- Set date related props ----- //
    // ? Docs: https://fullcalendar.io/docs/Event-setDates
    existingEvent.setDates(updatedEventData.start, updatedEventData.end, {
      allDay: updatedEventData.allDay,
    });

    // --- Set event's extendedProps ----- //
    // ? Docs: https://fullcalendar.io/docs/Event-setExtendedProp
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < extendedPropsToUpdate.length; index++) {
      const propName = extendedPropsToUpdate[index];
      existingEvent.setExtendedProp(
        propName,
        updatedEventData.extendedProps[propName]
      );
    }
  };

  // ------------------------------------------------
  // (UI) removeEventInCalendar
  // ------------------------------------------------
  const removeEventInCalendar = (eventId) => {
    toast({
      component: ToastificationContent,
      props: {
        title: "Event Removed",
        icon: "TrashIcon",
        variant: "danger",
      },
    });
    calendarApi.getEventById(eventId).remove();
  };

  // ------------------------------------------------
  // grabEventDataFromEventApi
  // ? It will return just event data from fullCalendar's EventApi which is not required for event mutations and other tasks
  // ! You need to update below function as per your extendedProps
  // ------------------------------------------------
  const grabEventDataFromEventApi = (eventApi) => {

    const { id, title, start, end, allDay, extendedProps = {} } = eventApi;

    // Helper function to format date as YYYY-MM-DD only (no time)
    const formatDateOnly = (date) => {
      if (!date) return "";
      if (typeof date === "string") return date.split("T")[0];
      return date.toISOString().split("T")[0];
    };

    // Extract all extended properties with defaults
    const {
      calendar,
      guests = [],
      location = "",
      description = "",
      classID = [0],
      secID = [0],
      departmentID = [0],
      startTime = "",
      endTime = "",
      eventLink = "",
      contactPhone = "",
      attachments = [],
    } = extendedProps;

    const eventData = {
      id,
      title,
      start: formatDateOnly(start), // Format as date only
      end: formatDateOnly(end), // Format as date only
      allDay,
      extendedProps: {
        calendar,
        guests,
        location,
        description,
        classID,
        secID,
        departmentID,
        startTime,
        endTime,
        eventLink,
        contactPhone,
        attachments,
      },
    };

    return eventData;
  };

  // ------------------------------------------------
  // addEvent
  // ------------------------------------------------
  const addEvent = (eventData) => {
    store.dispatch("calendar/addEvent", { event: eventData }).then(() => {
      // eslint-disable-next-line no-use-before-define
      refetchEvents();
    });
  };

  // ------------------------------------------------
  // updateEvent
  // ------------------------------------------------
  const updateEvent = (eventData) => {
    store
      .dispatch("calendar/updateEvent", { event: eventData })
      .then((response) => {
        const updatedEvent = response.data.event;

        const propsToUpdate = ["id", "title", "url"];
        const extendedPropsToUpdate = [
          "calendar",
          "guests",
          "location",
          "description",
          "classID",
          "secID",
        ];

        updateEventInCalendar(
          updatedEvent,
          propsToUpdate,
          extendedPropsToUpdate
        );
      });
  };

  // ------------------------------------------------
  // removeEvent
  // ------------------------------------------------
  const removeEvent = () => {
    const eventId = event.value.id;
    
    store.dispatch("calendar/removeEvent", { id: eventId, context: toast })
      .then((response) => {
        if (!response.cancelled) {
          if (response.success) {
            // Only remove from calendar if delete was successful
            calendarApi.getEventById(eventId).remove();
            
            // Show success notification
            toast({
              component: ToastificationContent,
              props: {
                title: 'Event Deleted',
                text: `Event has been deleted successfully`,
                icon: 'CheckIcon',
                variant: 'success',
              },
            });
          }
        }
      })
      .catch((error) => {
        console.error('❌ Error deleting event:', error);
        
        // Show error notification
        toast({
          component: ToastificationContent,
          props: {
            title: 'Delete Failed',
            text: 'Failed to delete event. Please try again.',
            icon: 'XIcon',
            variant: 'danger',
          },
        });
      });
  };

  // ------------------------------------------------
  // refetchEvents
  // ------------------------------------------------
  const refetchEvents = () => {
    calendarApi.refetchEvents();
  };

  // ------------------------------------------------
  // selectedCalendars
  // ------------------------------------------------
  const selectedCalendars = computed(
    () => store.state.calendar.selectedCalendars
  );

  watch(selectedCalendars, () => {
    refetchEvents();
  });

  // --------------------------------------------------------------------------------------------------
  // AXIOS: fetchEvents
  // * This will be called by fullCalendar to fetch events. Also this can be used to refetch events.
  // --------------------------------------------------------------------------------------------------
  const fetchEvents = (info, successCallback) => {
    // If there's no info => Don't make useless API call
    if (!info) return;

    // Get the year from the calendar's current view
    const currentYear = info.start.getFullYear();

    // Create the request object for the event-calendar API
    const requestData = {
      year: currentYear, // Use dynamic year from calendar view
      months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], // All months
    };

    // Make API call to EventsCalendar/Load using the correct store action
    const obj = {
      url:
        store.state.domain +
        "EventsCalendar/Load?db=" +
        store.state.userData.db +
        "&cID=" +
        store.state.userData.cId,
      body: requestData,
      token: store.state.userData.token,
      allResponse: true, // Get full response
      ignoreMessage: true, // Prevent toast notifications
    };

    store
      .dispatch("postObj", obj)
      .then((response) => {

        if (response && response.data) {
        }

        // Check different possible response formats
        let eventsData = null;

        if (
          response?.data?.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          eventsData = response.data.data;
        } else if (Array.isArray(response?.data)) {
          eventsData = response.data;
        } else if (Array.isArray(response)) {
          eventsData = response;
        } else {

          // Try to find events in nested objects
          const searchForEvents = (obj, path = "") => {
            if (Array.isArray(obj)) {
              return obj;
            }
            if (obj && typeof obj === "object") {
              for (const key in obj) {
                const result = searchForEvents(obj[key], `${path}.${key}`);
                if (result) return result;
              }
            }
            return null;
          };

          eventsData = searchForEvents(response, "response");
        }

        if (eventsData && Array.isArray(eventsData) && eventsData.length > 0) {

          // Transform the API response to match FullCalendar format
          const transformedEvents = eventsData
            .map((item, index) => {

              // Handle different possible event structures
              let event = null;
              if (item.events) {
                event = item.events;
              } else if (item.event) {
                event = item.event;
              } else {
                event = item; // Assume the item itself is the event
              }

              if (!event || !event.title) {
                return null;
              }

              // Find the color based on eventFor - fix the mapping
              const eventFor = event.eventFor || event.calendar || "Staff";

              // Define better colors for each calendar type - fixed mapping
              const getEventColors = (type) => {
                switch (type) {
                  case "Students":
                    return {
                      backgroundColor: "#e3f2fd", // Light blue background
                      borderColor: "#7367f0", // Blue border
                      textColor: "#1565c0", // Dark blue text
                    };
                  case "Staff":
                    return {
                      backgroundColor: "#ffebee", // Light red background
                      borderColor: "#ea5455", // Red border
                      textColor: "#c62828", // Dark red text
                    };
                  case "Parents":
                    return {
                      backgroundColor: "#fff3e0", // Light orange background
                      borderColor: "#ff9800", // Orange border
                      textColor: "#e65100", // Dark orange text
                    };
                  case "Holiday":
                    return {
                      backgroundColor: "#e8f5e8", // Light green background
                      borderColor: "#28c76f", // Green border
                      textColor: "#198754", // Dark green text
                    };
                  default:
                    return {
                      backgroundColor: "#f8f9fa", // Light gray background
                      borderColor: "#6c757d", // Gray border
                      textColor: "#495057", // Dark gray text
                    };
                }
              };

              const colors = getEventColors(eventFor);

              // Fix date handling for multi-day events
              // FullCalendar treats end dates as exclusive, so we need to add 1 day for all-day events
              let startDate = event.start || event.startDate;
              let endDate = event.end || event.endDate;
              let isAllDay = event.allDay || false;

              // If it's an all-day event and has an end date, add 1 day to make it inclusive
              if (isAllDay && endDate && startDate !== endDate) {
                const adjustedEndDate = new Date(endDate);
                adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
                endDate = adjustedEndDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
              }

              const transformedEvent = {
                id: event.id || `event-${index}`,
                title: event.title, // Show only the title, no time or location
                start: startDate,
                end: endDate,
                allDay: isAllDay,
                backgroundColor: colors.backgroundColor,
                borderColor: colors.borderColor,
                textColor: colors.textColor,
                extendedProps: {
                  calendar: eventFor,
                  description: event.description || "",
                  location: event.location || "",
                  eventLink: event.locationLink || event.eventLink || "",
                  startTime: event.startTime || "",
                  endTime: event.endTime || "",
                  classID: event.clsIDs
                    ? event.clsIDs.split(",").map(Number).filter(Boolean)
                    : [0],
                  secID: event.secIDs
                    ? event.secIDs.split(",").map(Number).filter(Boolean)
                    : [0],
                  departmentID: event.departmentIDs
                    ? event.departmentIDs.split(",").map(Number).filter(Boolean)
                    : [0],
                  contactPhone: event.contactPhone || "",
                  attachments: item.attachments || [],
                },
              };

              return transformedEvent;
            })
            .filter((event) => event !== null);

          // Filter events based on selected calendars
          const filteredEvents = transformedEvents.filter((event) => {
            const eventCalendar = event.extendedProps.calendar;
            const isSelected = selectedCalendars.value.includes(eventCalendar);
            return isSelected;
          });

          // Pass the filtered events to FullCalendar
          successCallback(filteredEvents);
        } else {
          successCallback([]);
        }
      })
      .catch((error) => {
        console.error("=== API ERROR ===");
        console.error("Error object:", error);
        console.error("Error message:", error.message);
        console.error("Error response:", error.response);
        console.error("Error stack:", error.stack);

        // Don't show toast notification as it's causing issues
        console.error("API call failed, returning empty events array");
        successCallback([]);
      });
  };

  // ------------------------------------------------------------------------
  // calendarOptions
  // * This isn't considered in UI because this is the core of calendar app
  // ------------------------------------------------------------------------
  const calendarOptions = ref({
    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
    initialView: "dayGridMonth",
    headerToolbar: {
      start: "sidebarToggle, prev,next, title",
      end: "", // Removed dayGridMonth button to hide "month" text
    },
    events: fetchEvents, // Use fetchEvents function like the working calendar

    /*
      Enable dragging and resizing event
      ? Docs: https://fullcalendar.io/docs/editable
    */
    editable: false,

    /*
      Enable resizing event from start
      ? Docs: https://fullcalendar.io/docs/eventResizableFromStart
    */
    eventResizableFromStart: false,

    /*
      Automatically scroll the scroll-containers during event drag-and-drop and date selecting
      ? Docs: https://fullcalendar.io/docs/dragScroll
    */
    dragScroll: false,

    /*
      Max number of events within a given day
      ? Docs: https://fullcalendar.io/docs/dayMaxEvents
    */
    dayMaxEvents: 2,

    /*
      Determines if day names and week names are clickable
      ? Docs: https://fullcalendar.io/docs/navLinks
    */
    navLinks: true,

    eventClassNames({ event: calendarEvent }) {
      const colorName = calendarEvent.extendedProps.calendar;

      // Return CSS classes for better styling - updated to match our CSS
      const getEventClasses = (type) => {
        switch (type) {
          case "Students":
            return ["fc-event-students"];
          case "Staff":
            return ["fc-event-staff"];
          case "Parents":
            return ["fc-event-parents"];
          case "Holiday":
            return ["fc-event-holiday"];
          default:
            return ["fc-event-default"];
        }
      };

      const classes = getEventClasses(colorName);
      return classes;
    },

    eventClick({ event: clickedEvent }) {
      // * Only grab required field otherwise it goes in infinity loop
      event.value = grabEventDataFromEventApi(clickedEvent);
      isEventHandlerSidebarActive.value = true;
    },

    customButtons: {
      sidebarToggle: {
        text: "sidebar",
        click() {
          isCalendarOverlaySidebarActive.value =
            !isCalendarOverlaySidebarActive.value;
        },
      },
    },

    dateClick(info) {
      // Format the clicked date as YYYY-MM-DD only (no time)
      const formatDateOnly = (date) => {
        return date.toISOString().split("T")[0];
      };

      event.value = JSON.parse(
        JSON.stringify(
          Object.assign(event.value, {
            start: info.date,
            end: info.date,
          })
        )
      );
      isEventHandlerSidebarActive.value = true;
    },

    /*
      Handle event drop (Also include dragged event)
      ? Docs: https://fullcalendar.io/docs/eventDrop
      ? We can use `eventDragStop` but it doesn't return updated event so we have to use `eventDrop` which returns updated event
    */
    eventDrop({ event: droppedEvent }) {
      updateEvent(grabEventDataFromEventApi(droppedEvent));
    },

    /*
      Handle event resize
      ? Docs: https://fullcalendar.io/docs/eventResize
    */
    eventResize({ event: resizedEvent }) {
      updateEvent(grabEventDataFromEventApi(resizedEvent));
    },

    // Get direction from app state (store)
    direction: computed(() => (store.state.appConfig.isRTL ? "rtl" : "ltr")),
    rerenderDelay: 350,

    /*
      Handle view/date changes - this ensures events are refetched when navigating between months/years
      ? Docs: https://fullcalendar.io/docs/datesSet
    */
    datesSet(dateInfo) {

      // Force refetch events when year changes (especially Dec to Jan navigation)
      if (calendarApi) {
        calendarApi.refetchEvents();
      }
    },
  });

  // ------------------------------------------------------------------------

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const isEventHandlerSidebarActive = ref(false);

  const isCalendarOverlaySidebarActive = ref(false);

  return {
    refCalendar,
    isCalendarOverlaySidebarActive,
    calendarOptions,
    event,
    clearEventData,
    addEvent,
    updateEvent,
    removeEvent,
    refetchEvents,
    fetchEvents,

    // ----- UI ----- //
    isEventHandlerSidebarActive,
  };
}
